/**
 * ConfidentialTransferPanel — display-only evidence panel for Token-2022
 * Confidential Transfers. Renders on the transaction-detail page when the tx
 * contains one or more confidential-transfer instructions.
 *
 * Design goal: show HONESTLY what is public vs hidden. It never tries to reveal
 * a hidden Transfer amount (that value is ElGamal ciphertext + ZK proof, not in
 * the instruction). For the public bridge ops (Deposit / Withdraw) it DOES show
 * the plaintext amount — with an explicit "amount visible here" marker — because
 * that is the honest, load-bearing point: confidential transfers hide the
 * amount, but the public bridge does not.
 */
import React from 'react'
import { Lock, Eye, EyeOff, ShieldCheck, AlertTriangle } from 'lucide-react'
import {
  recognizeConfidentialInstruction,
  isTokenReallocate,
  ZK_ELGAMAL_PROOF_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  type ConfidentialInstructionInfo,
} from '@/utils/confidentialTransfer'
import { resolveInstructionProgramId } from '@/utils/txSummary'
import { AddressDisplay } from '@/components/common/AddressDisplay'

interface RecognizedIx {
  index: number
  info: ConfidentialInstructionInfo
  accounts: string[]
}

function resolveAccounts(instruction: any, accountKeys: string[]): string[] {
  const raw = instruction?.accounts
  if (!Array.isArray(raw)) return []
  return raw
    .map((a: any) => (typeof a === 'number' ? accountKeys[a] : typeof a === 'string' ? a : a?.pubkey))
    .filter(Boolean)
}

/** Collect every confidential instruction across top-level + inner instructions. */
function collect(transaction: any, accountKeys: string[]): RecognizedIx[] {
  const out: RecognizedIx[] = []
  const scan = (ixs: any[]) => {
    if (!Array.isArray(ixs)) return
    ixs.forEach((ix, index) => {
      const pid = resolveInstructionProgramId(ix, accountKeys)
      const info = recognizeConfidentialInstruction(pid, ix.data)
      if (info) out.push({ index, info, accounts: resolveAccounts(ix, accountKeys) })
    })
  }
  scan(transaction?.instructions)
  if (Array.isArray(transaction?.innerInstructions)) {
    transaction.innerInstructions.forEach((inner: any) => scan(inner?.instructions))
  }
  return out
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:w-40 sm:flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-900 dark:text-gray-100 break-all">{children}</span>
  </div>
)

export const ConfidentialTransferPanel: React.FC<{ transaction: any; accountKeys: string[] }> = ({
  transaction,
  accountKeys,
}) => {
  const recognized = React.useMemo(() => collect(transaction, accountKeys), [transaction, accountKeys])
  if (recognized.length === 0) return null

  const hasTransfer = recognized.some((r) => r.info.visibility === 'hidden')
  const bridgeOps = recognized.filter((r) => r.info.visibility === 'public')
  const hasZkProgram = (accountKeys || []).includes(ZK_ELGAMAL_PROOF_PROGRAM_ID)

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 p-6">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
        <Lock className="h-4 w-4 text-teal-600 dark:text-teal-400" />
        <span>Confidential Transfer</span>
        <span className="rounded-pill bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
          Token-2022
        </span>
      </h3>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Amounts are encrypted with ElGamal and proven in zero-knowledge, verified on chain by the{' '}
        {hasZkProgram ? 'invoked ' : ''}ZK ElGamal Proof program.
      </p>

      {/* Public vs hidden */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            <Eye className="h-3.5 w-3.5" /> Public on chain
          </div>
          <div className="space-y-2">
            <Row label="Token program">
              <AddressDisplay address={TOKEN_2022_PROGRAM_ID} />
            </Row>
            {hasZkProgram && (
              <Row label="Proof verifier">
                <AddressDisplay address={ZK_ELGAMAL_PROOF_PROGRAM_ID} />
              </Row>
            )}
            {recognized.map((r) => (
              <Row key={r.index} label="Operation">
                <span className="font-medium">{r.info.name}</span>
                {r.info.visibility === 'hidden' && (
                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                    · {r.info.dataLen}-byte proof — amount proven in range, value encrypted
                  </span>
                )}
              </Row>
            ))}
            <Row label="Participants">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Token accounts, owner wallets & each holder&apos;s ElGamal public key are all public.
              </span>
            </Row>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            <EyeOff className="h-3.5 w-3.5" /> Hidden on chain
          </div>
          {hasTransfer ? (
            <div className="space-y-2">
              <Row label="Transfer amount">
                <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  <Lock className="h-3.5 w-3.5" /> encrypted under recipient&apos;s ElGamal key
                </span>
              </Row>
              <Row label="Balances">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Sender&apos;s post-transfer balance and the recipient&apos;s balance stay ciphertext.
                </span>
              </Row>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No confidential transfer amount in this transaction — only the public bridge/setup steps below.
            </p>
          )}
        </div>
      </div>

      {/* Bridge ops leak the amount — say so, and show it. */}
      {bridgeOps.length > 0 && (
        <div className="mt-4 space-y-2">
          {bridgeOps.map((r) => (
            <div
              key={r.index}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900/40 dark:bg-amber-900/20"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="font-medium text-gray-900 dark:text-gray-100">{r.info.name}:</span>
              {r.info.amount ? (
                <span className="font-mono text-gray-900 dark:text-gray-100">{r.info.amount.ui}</span>
              ) : (
                <span className="text-gray-600 dark:text-gray-300">public amount</span>
              )}
              <span className="text-xs text-amber-700 dark:text-amber-300">— amount visible here (public bridge)</span>
            </div>
          ))}
        </div>
      )}

      {/* Honesty banner */}
      <div className="mt-4 flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
        <span>
          <strong className="text-gray-800 dark:text-gray-200">What this hides:</strong> the transfer amount only. It
          does <strong>not</strong> hide the participants (sender, recipient, and that a transfer happened are all
          public), and amounts are fully <strong>public at any Deposit / Withdraw</strong> bridge step. Amounts stay
          private only while value circulates confidentially between accounts.
        </span>
      </div>
    </div>
  )
}

export default ConfidentialTransferPanel
